import * as React from "react"
import { connect } from "react-redux"
import trans from "trans"

import {
    css,
    Grid,
    GridCell,
    Section,
    TextInput,
    Select,
    InputGroup,
    Icon,
    Theme,
} from "@bdfx/mergado-ui-kit"

import {
    updateInputValue as change,
} from "app/reducers/form";

import { Project } from "app/types/model-types";

import { ReduxList } from "app/types/app-types";
import { setProjectToStore } from "app/reducers/project"
import store from "store"
import { getMergadoElementsSize, getNecessaryElementsForFIE, saveAdvancedSettings } from "app/helpers/api"
import { AxiosResponse } from "axios"
import { createToast } from "actions"
import { textToast } from "app/helpers/toast_factory"
import { allowSuperadminForUsers } from "template-edit/components/helper"
import { debounce, escapeRegExp } from "lodash"

const ALL_APPLY_ELEMENTS_TYPES = ['all', 'exclude_some', 'regexp', 'necessary', 'selected_mergado_equivalent']
export type ApplyElementsType = 'necessary' | 'selected_mergado_equivalent' | 'exclude_some' | 'all' | 'regexp'

const MINIMAL_MERGADO_XML_EQUIVALENT = [
    'ITEM_ID',
    'LINK',
    'IMAGE',
    'IMAGE_ALTERNATIVE',
    'NAME_EXACT',
    'NAME_COMMERCIAL',
    'EAN',
    'URL',
    'PRICE',
    'PRICE_VAT'
]

const ALL_MERGADO_XML_EQUIVALENTS = [
    'ITEM_ID',
    'LINK',
    'PRICE',
    'IMAGE',
    'IMAGE_ALTERNATIVE',
    'NAME_EXACT',
    'NAME_COMMERCIAL',
    'EAN',
    'URL',
    'VAT', 'PRICE', 'PRICE_VAT', 'PRICE_DISCOUNT', 'PRICE_RETAIL', 'PRICE_RETAIL_VAT',
    'DELIVERY', 'CURRENCY', 'ITEMGROUP_ID', 'SIZE', 'PRODUCTNO', 'PARAM', 'CATEGORY', 'BRAND',
];

interface Props {
    change: typeof change
    project: ReduxList<Project>
}

interface State {
    maxPayloadSize: number
    elementRegexp: string
    xmlEquivalent: string[]
    excludeList: string[]
    elements: string[]
    necessaryElements: string[]
    applyElements: ApplyElementsType
    isLoading: boolean
    isSaving: boolean
}

class AdvancedProjectSettings extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props)

        this.state = this.getStateFromProps(props)
    }

    componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {
        if (prevProps && prevProps.project.data && this.props.project.data && (
            prevProps.project.data.mergado_max_payload_size !== this.props.project.data.mergado_max_payload_size ||
            prevProps.project.data.mergado_element_regex !== this.props.project.data.mergado_element_regex ||
            prevProps.project.data.mergado_xml_equivalent !== this.props.project.data.mergado_xml_equivalent
        )) {
            this.setState({ ...this.getStateFromProps(this.props) })
        }
    }

    getStateFromProps = (props: Props): State => {
        return {
            maxPayloadSize: props.project.data?.mergado_max_payload_size,
            elementRegexp: props.project.data?.mergado_element_regex,
            xmlEquivalent: props.project.data?.mergado_xml_equivalent ? props.project.data?.mergado_xml_equivalent.split(',') : MINIMAL_MERGADO_XML_EQUIVALENT,
            excludeList: this.state?.excludeList ? this.state.excludeList : [],
            isSaving: this.state?.isSaving ? this.state.isSaving : null,
            isLoading: this.state?.isLoading ? this.state.isLoading : null,
            elements: this.state?.elements ? this.state.elements : null,
            necessaryElements: this.state?.necessaryElements ? this.state.necessaryElements : null,
            applyElements: this.state?.applyElements ? this.state.applyElements :
                (props.project.data.mergado_element_regex ? 'regexp' : (props.project.data.mergado_xml_equivalent ? 'selected_mergado_equivalent' : 'all'))
        }
    }

    saveDebounced = debounce(() => {
        this.handleSave()
    }, 800)

    handleSave = (): void => {
        const { maxPayloadSize, elementRegexp, xmlEquivalent, applyElements, excludeList, isSaving } = this.state

        if (!isSaving) {
            this.setState({ isSaving: true })
            if (applyElements === 'exclude_some') {
                const newElementRegexp = excludeList.length > 0 ? `^((?!${excludeList.map(e => escapeRegExp(e)).join('|')}).)*$` : null
                saveAdvancedSettings(maxPayloadSize, newElementRegexp, null, 'regexp')
                    .then((response: AxiosResponse<Project>) => {
                        store.dispatch(setProjectToStore({
                            ...this.props.project.data,
                            ...response.data,
                        }))
                    })
                    .catch(error => {
                        console.error('cannot save advanced project settings', error)
                    })
                    .finally(() => {
                        this.setState({ isSaving: false })
                    })
            } else {
                saveAdvancedSettings(maxPayloadSize, elementRegexp, xmlEquivalent, applyElements)
                    .then((response: AxiosResponse<Project>) => {
                        store.dispatch(setProjectToStore({
                            ...this.props.project.data,
                            ...response.data,
                        }))
                    })
                    .catch(error => {
                        console.error('cannot save advanced project settings', error)
                    })
                    .finally(() => {
                        this.setState({ isSaving: false })
                    })
            }
        }
    }

    componentDidMount() {
        this.getData()
    }

    getData = () => {
        this.setState({ isLoading: true })
        getMergadoElementsSize()
            .then((response: AxiosResponse<string[]>) => {
                this.setState({ elements: response.data })
            })
            .catch(error => {
                console.error(error)
                store.dispatch(createToast(textToast('error', error.response ?? '')))
            })
            .finally(() => {
                this.setState({ isLoading: false })
            })

        this.setState({ isLoading: true })
        getNecessaryElementsForFIE()
            .then((response: AxiosResponse<string[]>) => {
                this.setState({ necessaryElements: response.data })
            })
            .catch(error => {
                console.error(error)
                store.dispatch(createToast(textToast('error', error.response ?? '')))
            })
            .finally(() => {
                this.setState({ isLoading: false })
            })
    }

    renderApplyElements = (): JSX.Element => {
        const { applyElements, elementRegexp, xmlEquivalent, excludeList, elements } = this.state

        const xmlEquivalentOptions = ALL_MERGADO_XML_EQUIVALENTS.map(o => { return { value: o, label: o } })
        const excludeOptions = elements && elements.length ? elements.map(o => { return { value: o, label: o } }) : null
        const missingElements = xmlEquivalent.length ? MINIMAL_MERGADO_XML_EQUIVALENT.map(e => !xmlEquivalent.includes(e) ? e : null).filter(e => e).join(',') : null

        switch (applyElements) {
            case 'exclude_some':
                return <React.Fragment>
                    <GridCell></GridCell>
                    <GridCell>
                        {excludeOptions &&
                            <Select
                                options={excludeOptions}
                                value={excludeList}
                                multiple={true}
                                onChange={(e) => {
                                    this.setState({ excludeList: e })
                                    this.saveDebounced()
                                }}
                                onClear={() => {
                                    this.setState({ excludeList: [] })
                                    this.saveDebounced()
                                }}
                            />
                        }
                    </GridCell>
                </React.Fragment>
            case 'regexp':
                return <React.Fragment>
                    <GridCell>{trans('mergado_element_regex')}</GridCell>
                    <GridCell>
                        <TextInput
                            placeholder="e.g.: ^((?!DESCRIPTION|Popis).)*$ to exclude description and popis"
                            value={elementRegexp}
                            size={'small'}
                            onClear={() => {
                                this.setState({ elementRegexp: null })
                                this.saveDebounced()
                            }}
                            onChange={(e) => {
                                this.setState({ elementRegexp: e })
                                this.saveDebounced()
                            }}
                        />
                    </GridCell>
                </React.Fragment>
            case 'selected_mergado_equivalent':
                return <React.Fragment>
                    <GridCell>{trans('mergado_xml_equivalent')}</GridCell>
                    <GridCell>
                        <Select
                            options={xmlEquivalentOptions}
                            value={xmlEquivalent}
                            multiple={true}
                            error={missingElements ? missingElements : undefined}
                            invalid={!!missingElements}
                            onChange={(e) => {
                                this.setState({ xmlEquivalent: e })
                                this.saveDebounced()
                            }}
                            onClear={() => {
                                this.setState({ xmlEquivalent: [] })
                                this.saveDebounced()
                            }}
                        />
                    </GridCell>
                </React.Fragment>
        }
    }

    render() {
        const { maxPayloadSize, elements, necessaryElements, applyElements, isSaving, isLoading } = this.state
        const { project } = this.props

        return <StyledSection header={<Icon type={'wrench'} text={trans('advanced_settings')} size={24} />} suffix={<Icon type="code-radix" size={18} />}>
            <Grid cols={'1fr 1fr'} colgap={20}>
                <Grid cols={'minmax(150px, auto) max-content'} colgap={20} rowgap={10}>
                    <GridCell>{trans('mergado_apply_elements')}</GridCell>
                    <GridCell>
                        <InputGroup
                            orientation="horizontal"
                            singleChoice={true}
                            items={ALL_APPLY_ELEMENTS_TYPES.map((s) => {
                                return { value: s, label: trans(s) }
                            })}
                            value={applyElements}
                            onChange={(e) => {
                                this.setState({ applyElements: e })
                                this.saveDebounced()
                            }}
                            onClear={() => {
                                this.setState({
                                    applyElements: 'all',
                                    elementRegexp: null,
                                    xmlEquivalent: [],
                                })
                                this.saveDebounced()
                            }}
                        />
                    </GridCell>

                    {this.renderApplyElements()}

                    {/* docasne skryto */}
                    {project.data.user?.id && allowSuperadminForUsers.includes(project.data.user.id) &&
                        <React.Fragment>
                            <GridCell className="some-users-only">{trans('mergado_max_payload_size')}</GridCell>
                            <GridCell className="some-users-only">
                                <TextInput
                                    type='number'
                                    placeholder="max 15 000 000 characters"
                                    value={maxPayloadSize}
                                    min={1000000}
                                    max={15000000}
                                    step={1000000}
                                    size={'small'}
                                    onClear={() => {
                                        this.setState({ maxPayloadSize: null })
                                        this.saveDebounced()
                                    }}
                                    onChange={(e) => {
                                        this.setState({ maxPayloadSize: e })
                                        this.saveDebounced()
                                    }}
                                />
                            </GridCell>
                        </React.Fragment>
                    }

                    {/* <GridCell></GridCell>
                    <GridCell style={{ textAlign: 'right !important' }}>
                        <Button
                            icon={isSaving ? 'spinner' : 'check-slim'}
                            disabled={isSaving}
                            type={'void'}
                            onClick={() => this.handleSave()}
                            size={'small'}
                            color={Theme.green}
                        >{trans('save')}</Button>
                    </GridCell> */}
                </Grid>

                <GridCell>
                    <Grid cols={'1fr 1fr'} className={isLoading ? 'muk-helper-loading' : ''}>
                        <GridCell>
                            {elements &&
                                <React.Fragment>
                                    <h3>{trans('suggested_elements_to_be_excluded')}</h3>
                                    <ul style={{ overflow: 'auto' }}>{elements.map(e => <li key={e}>{e}</li>)}</ul>
                                </React.Fragment>
                            }

                            {necessaryElements &&
                                <React.Fragment>
                                    <h3 style={{ color: this.state.applyElements === 'necessary' ? Theme.red : '#000' }}>{trans('necessary_elements')}</h3>
                                    <ul style={{ overflow: 'auto', color: this.state.applyElements === 'necessary' ? Theme.red : '#000' }}>{necessaryElements.map(e => <li key={e}>{e}</li>)}</ul>
                                </React.Fragment>
                            }
                        </GridCell>
                        <GridCell></GridCell>
                    </Grid>
                </GridCell>
            </Grid>
        </StyledSection>
    }
}


const StyledSection = css(Section)({
    gridColumn: '1/-1',
    whiteSpace: 'normal',
    ' .muk-gridcell': {
        textAlign: 'left',
        alignSelf: 'center',
    },
    ' .muk-inputgroup .m-item': {
        width: '80px',
    }
})

function mapStateToProps(state) {
    return {
        project: state.project,
    }
}

export default connect(mapStateToProps, {
    change,
})(AdvancedProjectSettings)

